import { IDeploymentAndValueStatsSavingsAggregate } from '@kcftech/prometheustypescriptapiclient';
import { BarChartLegend } from 'pages/Dashboard/components/BarChartLegend';
import { ScrollableBarChart } from 'pages/Dashboard/components/ScrollableBarChart';
import { ReactElement, ReactNode, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { useThemeValues } from 'styles/theme';
import { sortBy } from 'utils/array';

/**
 * Parses a month string in "YYYY-MM" format to a Date object.
 * Appends '-01' to create a valid date at the first day of the month.
 * @param monthKey - Month string in "YYYY-MM" format (e.g., "2025-12")
 * @returns Date object or null if invalid
 */
export function parseMonthKey(monthKey: string): Date | null {
    const date = new Date(monthKey + '-01');
    return isNaN(date.getTime()) ? null : date;
}

const formatCurrency = (value: ReactNode): ReactNode => {
    if (typeof value !== 'number') {
        return value;
    }
    if (value >= 1_000_000) {
        return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
        return `$${(value / 1_000).toFixed(1)}K`;
    }
    return `$${value}`;
};

const monthTickFormatter = (tick: string | number | Date): string => {
    // Handle date strings in format "YYYY-MM"
    const date = parseMonthKey(String(tick));
    if (!date) {
        return String(tick);
    }
    return date.toLocaleString('default', { month: 'short' });
};

const createRenderQuarterTick = (strokeColor: string) => (tickProps: {
    x: number;
    y: number;
    payload: { value: string; offset: number };
    width: number;
    visibleTicksCount: number;
}): ReactElement => {
    const { x, y, payload, width, visibleTicksCount } = tickProps;
    const { value, offset } = payload;
    const date = parseMonthKey(value);
    if (!date) {
        return <></>;
    }

    const month = date.getMonth();
    const quarterNo = Math.floor(month / 3) + 1;

    if (month % 3 === 1) {
        // place quarter text near the middle
        return (
            <text
                x={x + width / visibleTicksCount / 2 - offset}
                y={y - 4}
                textAnchor="middle"
            >
                {`Q${quarterNo}`}
            </text>
        );
    }

    const isLast = month === 11;

    if (month % 3 === 0 || isLast) {
        const pathX =
            Math.floor(isLast ? x - offset + width / visibleTicksCount : x - offset) +
            0.5;

        return <path d={`M${pathX},${y - 4}v${-35}`} stroke={strokeColor} />;
    }
    return <></>;
};

const createRenderYearTick = (strokeColor: string) => (tickProps: {
    x: number;
    y: number;
    payload: { value: string; offset: number };
    width: number;
    visibleTicksCount: number;
}): React.ReactElement => {
    const { x, y, payload, width, visibleTicksCount } = tickProps;
    const { value, offset } = payload;
    const date = parseMonthKey(value);
    if (!date) {
        return <></>;
    }

    const month = date.getMonth();
    const year = date.getFullYear();

    // Show year label only in June (month index 5)
    if (month === 5) {
        return (
            <text
                x={x + width / visibleTicksCount / 2 - offset}
                y={y}
                textAnchor="middle"
            >
                {year}
            </text>
        );
    }

    // Draw vertical line at year boundaries (January)
    if (month === 0) {
        const pathX = Math.floor(x - offset) + 0.5;
        return (
            <path
                d={`M${pathX},${y - 12}v${-55}`}
                stroke={strokeColor}
                strokeWidth={1.5}
            />
        );
    }

    return <></>;
};

export function ValueOverTime({
    data,
}: {
    /** key is a month identifier like 2025-12 */
    data: IDeploymentAndValueStatsSavingsAggregate[];
}) {
    const theme = useThemeValues();

    const renderQuarterTick = useMemo(
        () => createRenderQuarterTick(theme.navBorderColor),
        [theme.navBorderColor]
    );

    const renderYearTick = useMemo(() => createRenderYearTick(theme.secondaryText), [
        theme.secondaryText,
    ]);

    const mappedData = useMemo(
        () =>
            sortBy(
                data.filter((d) => {
                    // Filter out entries with invalid dates or values
                    // TODO @jp: verify this isn't super common
                    if (!d.Key || isNaN(d.MonetaryValue)) {
                        return false;
                    }
                    // Validate date format (should be YYYY-MM)
                    return parseMonthKey(d.Key) !== null;
                }),
                (d) => d.Key
            ).map((d) => ({
                date: d.Key,
                value: d.MonetaryValue,
            })),
        [data]
    );

    return (
        <ScrollableBarChart
            yAxisWidth={80}
            dataLength={mappedData.length}
            minBarWidthPercent={10}
            startScrolledToEnd
            legend={
                <BarChartLegend
                    items={[
                        { label: 'Savings ($)', color: theme.dashboardAccentPrimary },
                    ]}
                />
            }
        >
            {({ yAxisWidth }) => (
                <BarChart data={mappedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        tickFormatter={monthTickFormatter}
                        height={34}
                    />
                    <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={renderQuarterTick}
                        height={14}
                        scale="band"
                        xAxisId="quarter"
                    />
                    <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={renderYearTick}
                        height={5}
                        scale="band"
                        xAxisId="year"
                    />
                    <YAxis
                        width={yAxisWidth}
                        tickFormatter={(v) => '$' + v?.toLocaleString()}
                    />
                    <Tooltip
                        formatter={(value: number) => `$${value.toLocaleString()}`}
                    />

                    <Bar
                        dataKey="value"
                        fill={theme.dashboardAccentPrimary}
                        name="Savings ($)"
                        label={{
                            position: 'top',
                            formatter: formatCurrency,
                            fontSize: 11,
                        }}
                        radius={[5, 5, 0, 0]}
                    />
                </BarChart>
            )}
        </ScrollableBarChart>
    );
}
